import { DeleteOutlined, PlusOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { Checkbox, Form, Image, Input, Tooltip, Typography } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import React from 'react';
import AuthenticatedAttachmentImage from '../../../components/authenticated-attachment-image';
import DrawerButtons from '../../../components/drawer-buttons';
import { formItemLayoutTool } from '../../../config/formLayouts';
import { translations } from '../../../config/translations';
import { showError, showSuccess } from '../../../helpers/NotificationHelper';
import {
    ApiException,
    AttachmentsClient,
    CreateToolCommandOfTool,
    ToolDetailVm,
    ToolsClient,
    UpdateToolCommandOfTool,
} from '../../../utils/api';

const { TextArea } = Input;

const MEDIA_TILE_PX = 112;

const mediaTileShellStyle: React.CSSProperties = {
    position: 'relative',
    width: MEDIA_TILE_PX,
    height: MEDIA_TILE_PX,
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #f0f0f0',
    background: '#fafafa',
    flexShrink: 0,
};

const mediaStarIconStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: 4,
    left: 4,
    cursor: 'pointer',
    fontSize: 18,
    zIndex: 2,
    lineHeight: 1,
    filter: 'drop-shadow(0 0 1px rgba(255,255,255,0.9))',
};

const mediaDeleteIconStyle: React.CSSProperties = {
    position: 'absolute',
    top: 4,
    right: 4,
    color: '#ff4d4f',
    cursor: 'pointer',
    background: 'rgba(255,255,255,0.92)',
    padding: 4,
    borderRadius: 4,
    fontSize: 14,
    zIndex: 2,
    lineHeight: 1,
};

const PendingMediaTile: React.FC<{
    file: UploadFile;
    onRemove: () => void;
    isCover: boolean;
    onToggleCover: () => void;
}> = ({ file, onRemove, isCover, onToggleCover }) => {
    const [src, setSrc] = React.useState<string>();

    React.useEffect(() => {
        const raw = file.originFileObj;
        if (!raw) return;
        const u = URL.createObjectURL(raw);
        setSrc(u);
        return () => URL.revokeObjectURL(u);
    }, [file.uid, file.originFileObj]);

    return (
        <div style={mediaTileShellStyle}>
            {src ? (
                <Image
                    src={src}
                    alt={file.name}
                    width={MEDIA_TILE_PX}
                    height={MEDIA_TILE_PX}
                    style={{ objectFit: 'cover' }}
                    preview
                />
            ) : null}
            <Tooltip title={translations.tools.listThumbnailStar}>
                <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleCover();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            onToggleCover();
                        }
                    }}
                    style={mediaStarIconStyle}
                >
                    {isCover ? (
                        <StarFilled style={{ color: '#faad14' }} />
                    ) : (
                        <StarOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                    )}
                </span>
            </Tooltip>
            <DeleteOutlined onClick={onRemove} style={mediaDeleteIconStyle} />
        </div>
    );
};

type CoverSelection = { kind: 'auto' } | { kind: 'pending'; uid: string };

export interface ToolFormProps {
    tool?: ToolDetailVm;
    onSuccess: (tool: ToolDetailVm) => void;
    onClose: () => void;
}

interface State {
    isSaving: boolean;
    pendingMultimediaFiles: UploadFile[];
    coverSelection: CoverSelection;
}

class ToolForm extends React.Component<ToolFormProps, State> {
    private formRef: any;
    private multimediaInputRef = React.createRef<HTMLInputElement>();

    public constructor(props: ToolFormProps) {
        super(props);

        this.formRef = React.createRef();

        this.state = {
            isSaving: false,
            pendingMultimediaFiles: [],
            coverSelection: { kind: 'auto' },
        };
    }

    private togglePendingCover = (uid: string): void => {
        this.setState((s) => {
            if (s.coverSelection.kind === 'pending' && s.coverSelection.uid === uid) {
                return { coverSelection: { kind: 'auto' } };
            }
            return { coverSelection: { kind: 'pending', uid } };
        });
    };

    private resolveThumbnailForCreate(
        newAttachmentIds: number[],
        pendingFiles: UploadFile[]
    ): number | null {
        const { coverSelection } = this.state;
        if (coverSelection.kind === 'auto') {
            return null;
        }
        const idx = pendingFiles.findIndex((f) => f.uid === coverSelection.uid);
        if (idx < 0 || idx >= newAttachmentIds.length) {
            return null;
        }
        return newAttachmentIds[idx]!;
    }

    private buildMultimediaSummary = (): string => {
        const pending = this.state.pendingMultimediaFiles;
        const count = pending.length;
        let bytes = 0;
        pending.forEach((f) => {
            bytes += f.size ?? f.originFileObj?.size ?? 0;
        });
        const mb = (bytes / (1024 * 1024)).toFixed(2);
        return translations.tools.multimediaSummary.replace('{count}', String(count)).replace('{size}', mb);
    };

    private handleSubmit = async (values: any) => {
        const { tool, onSuccess } = this.props;
        const { pendingMultimediaFiles } = this.state;

        this.setState({ isSaving: true });

        try {
            const client = new ToolsClient();
            const attachmentsClient = new AttachmentsClient();

            if (tool?.id) {
                const updateCmd = new UpdateToolCommandOfTool();
                updateCmd.id = tool.id;
                updateCmd.name = values.name;
                updateCmd.description = values.description;
                updateCmd.isArchived = !!values.isArchived;
                updateCmd.thumbnailAttachmentId =
                    tool.coverAttachmentId == null ? (null as any) : tool.coverAttachmentId;

                await client.update(tool.id, updateCmd);
                const refreshed = await client.getById(tool.id);
                showSuccess(translations.tools.saveSuccess);
                this.setState({ isSaving: false });
                onSuccess(refreshed);
                return;
            }

            const createCmd = new CreateToolCommandOfTool();
            createCmd.name = values.name;
            createCmd.description = values.description;
            createCmd.isArchived = !!values.isArchived;
            const created = await client.create(createCmd);
            const toolId = created.id;

            let refreshed: ToolDetailVm = created;
            try {
                const newAttachmentIds: number[] = [];
                for (const f of pendingMultimediaFiles) {
                    const raw = f.originFileObj;
                    if (raw) {
                        const vm = await attachmentsClient.create(
                            { data: raw, fileName: raw.name },
                            '',
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            toolId,
                            true
                        );
                        if (vm.id != null) {
                            newAttachmentIds.push(vm.id);
                        }
                    }
                }

                const thumbId = this.resolveThumbnailForCreate(newAttachmentIds, pendingMultimediaFiles);

                if (pendingMultimediaFiles.length > 0 || thumbId !== null) {
                    const updateCmd = new UpdateToolCommandOfTool();
                    updateCmd.id = toolId;
                    updateCmd.name = values.name;
                    updateCmd.description = values.description;
                    updateCmd.isArchived = !!values.isArchived;
                    updateCmd.thumbnailAttachmentId = thumbId === null ? (null as any) : thumbId;

                    await client.update(toolId, updateCmd);
                    refreshed = await client.getById(toolId);
                }
            } catch (postCreateErr) {
                try {
                    await client.delete(toolId);
                } catch {
                    // swallow rollback failure; original error is what matters
                }
                throw postCreateErr;
            }

            showSuccess(translations.tools.saveSuccess);
            this.setState({
                pendingMultimediaFiles: [],
                isSaving: false,
                coverSelection: { kind: 'auto' },
            });
            onSuccess(refreshed);
        } catch (error) {
            this.setState({ isSaving: false });
            if (error instanceof ApiException) {
                showError(error.response);
            } else {
                showError(translations.general.errorSavingData);
            }
        }
    };

    private openMultimediaPicker = () => {
        this.multimediaInputRef.current?.click();
    };

    private appendImageFiles = (rawFiles: FileList | File[]) => {
        const list = Array.from(rawFiles);
        const additions: UploadFile[] = [];
        let rejected = false;
        for (let i = 0; i < list.length; i++) {
            const raw = list[i];
            if (!raw.type.startsWith('image/')) {
                rejected = true;
                continue;
            }
            additions.push({
                uid: `pending-${Date.now()}-${i}-${raw.name}`,
                name: raw.name,
                status: 'done',
                originFileObj: raw as UploadFile['originFileObj'],
            });
        }
        if (rejected) {
            showError(translations.general.wrongFileType);
        }
        if (additions.length) {
            this.setState((s) => ({
                pendingMultimediaFiles: [...s.pendingMultimediaFiles, ...additions],
            }));
        }
    };

    private handleMultimediaInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = e.target;
        if (files?.length) {
            this.appendImageFiles(files);
        }
        e.target.value = '';
    };

    private handleMultimediaDragOver = (ev: React.DragEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
    };

    private handleMultimediaDrop = (ev: React.DragEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        const { files } = ev.dataTransfer;
        if (files?.length) {
            this.appendImageFiles(files);
        }
    };

    private removePendingMultimedia = (uid: string) => {
        this.setState((s) => {
            const nextFiles = s.pendingMultimediaFiles.filter((f) => f.uid !== uid);
            const nextCover =
                s.coverSelection.kind === 'pending' && s.coverSelection.uid === uid
                    ? ({ kind: 'auto' } as CoverSelection)
                    : s.coverSelection;
            return { pendingMultimediaFiles: nextFiles, coverSelection: nextCover };
        });
    };

    public render() {
        const { tool, onClose } = this.props;
        const { isSaving, pendingMultimediaFiles, coverSelection } = this.state;
        const isCreate = !tool?.id;

        return (
            <Form onFinish={this.handleSubmit} {...formItemLayoutTool} ref={this.formRef}>
                <DrawerButtons isSaving={isSaving} onCancelAction={onClose} />

                <Form.Item
                    name="name"
                    label={translations.projects.name}
                    initialValue={tool?.name}
                    rules={[
                        {
                            required: true,
                            message: translations.general.requiredField,
                        },
                        { max: 200, message: translations.general.maxLength200 },
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="description"
                    label={translations.projects.description}
                    initialValue={tool?.description}
                >
                    <TextArea rows={4} />
                </Form.Item>

                <Form.Item
                    name="isArchived"
                    valuePropName="checked"
                    label={translations.news.archived}
                    initialValue={tool ? tool.isArchived : false}
                >
                    <Checkbox />
                </Form.Item>

                {isCreate && (
                    <Form.Item label={translations.tools.multimedia}>
                        <input
                            ref={this.multimediaInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            style={{ display: 'none' }}
                            onChange={this.handleMultimediaInputChange}
                        />
                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 12,
                                alignItems: 'flex-start',
                            }}
                        >
                            {pendingMultimediaFiles.map((f) => (
                                <PendingMediaTile
                                    key={f.uid}
                                    file={f}
                                    onRemove={() => this.removePendingMultimedia(f.uid)}
                                    isCover={
                                        coverSelection.kind === 'pending' &&
                                        coverSelection.uid === f.uid
                                    }
                                    onToggleCover={() => this.togglePendingCover(f.uid)}
                                />
                            ))}
                            <div
                                role="button"
                                tabIndex={0}
                                onClick={this.openMultimediaPicker}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        this.openMultimediaPicker();
                                    }
                                }}
                                onDragOver={this.handleMultimediaDragOver}
                                onDrop={this.handleMultimediaDrop}
                                style={{
                                    width: MEDIA_TILE_PX,
                                    height: MEDIA_TILE_PX,
                                    borderRadius: 8,
                                    border: '1px dashed #d9d9d9',
                                    background: '#fafafa',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 6,
                                    cursor: 'pointer',
                                    color: 'rgba(0,0,0,0.45)',
                                    flexShrink: 0,
                                    transition: 'border-color 0.2s, color 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = '#1890ff';
                                    e.currentTarget.style.color = '#1890ff';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = '#d9d9d9';
                                    e.currentTarget.style.color = 'rgba(0,0,0,0.45)';
                                }}
                            >
                                <PlusOutlined style={{ fontSize: 22 }} />
                                <Typography.Text
                                    type="secondary"
                                    style={{ fontSize: 11, textAlign: 'center', padding: '0 6px' }}
                                >
                                    {translations.tools.addMediaCardHint}
                                </Typography.Text>
                            </div>
                        </div>
                        <Typography.Text
                            type="secondary"
                            style={{ fontSize: 12, display: 'block', marginTop: 8 }}
                        >
                            {translations.tools.multimediaPersistHint}
                        </Typography.Text>
                        <Typography.Text
                            type="secondary"
                            style={{ fontSize: 12, display: 'block', marginTop: 4 }}
                        >
                            {translations.tools.listThumbnailHint}
                        </Typography.Text>
                        <Typography.Text
                            type="secondary"
                            style={{ fontSize: 12, display: 'block', marginTop: 4 }}
                        >
                            {this.buildMultimediaSummary()}
                        </Typography.Text>
                    </Form.Item>
                )}
            </Form>
        );
    }
}

export default ToolForm;
